export interface SubscriptionAmountOffBump {
  base: number;
  billingType?: string | null;
  reached: boolean;
}

export interface SubscriptionAmountOffShares {
  primaryRecurringDiscount: number;
  oneTimeVariantsDiscount: number;
  /** In `bumps` order; 0 for a bump the coupon does not reach. */
  bumpDiscounts: number[];
}

/**
 * Splits one pool's share by size: each bump rounded and capped at its own base, the rest to the
 * pool's own line. A capped own line (the one-time variants) passes what it cannot take to the
 * bumps in order.
 */
const splitPool = (share: number, ownBase: number, bumpBases: number[], ownCapped: boolean) => {
  const pool = ownBase + bumpBases.reduce((sum, base) => sum + base, 0);
  if (pool <= 0 || share <= 0) return { own: Math.max(0, share), bumps: bumpBases.map(() => 0) };

  const bumps = bumpBases.map(base => Math.min(base, Math.round((share * base) / pool)));
  let own = share - bumps.reduce((sum, amount) => sum + amount, 0);

  for (let index = 0; index < bumps.length && own < 0; index += 1) {
    const taken = Math.min(bumps[index], -own);
    bumps[index] -= taken;
    own += taken;
  }

  if (ownCapped) {
    for (let index = 0; index < bumps.length && own > ownBase; index += 1) {
      const moved = Math.min(bumpBases[index] - bumps[index], own - ownBase);
      bumps[index] += moved;
      own -= moved;
    }
  }

  return { own, bumps };
};

/**
 * An amount off on a subscription is split by size, as Stripe splits it on an invoice. The bumps it
 * reaches join that split: a recurring bump takes part of the recurring share with the main price,
 * a one-time bump part of the one-time share with the one-time variants.
 */
export function shareSubscriptionAmountOff({
  recurringShare,
  oneTimeShare,
  primaryRecurringBase,
  oneTimeVariantsBase,
  bumps,
}: {
  recurringShare: number;
  oneTimeShare: number;
  primaryRecurringBase: number;
  oneTimeVariantsBase: number;
  bumps: readonly SubscriptionAmountOffBump[];
}): SubscriptionAmountOffShares {
  const isRecurring = (bump: SubscriptionAmountOffBump) => bump.billingType === 'recurring';
  const poolBases = (recurring: boolean) =>
    bumps.map(bump =>
      bump.reached && isRecurring(bump) === recurring ? Math.max(0, bump.base) : 0,
    );

  const recurringPool = splitPool(recurringShare, primaryRecurringBase, poolBases(true), false);
  const oneTimePool = splitPool(oneTimeShare, oneTimeVariantsBase, poolBases(false), true);

  return {
    primaryRecurringDiscount: recurringPool.own,
    oneTimeVariantsDiscount: oneTimePool.own,
    bumpDiscounts: bumps.map((bump, index) =>
      isRecurring(bump) ? recurringPool.bumps[index] : oneTimePool.bumps[index],
    ),
  };
}
